import { Field, FieldDescription, FieldError, FieldLabel } from 'components/ui/field';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { ConfidentialityDecision } from 'types/caseClosing';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

export interface ConfidentialitySectionProps {
    decision: ConfidentialityDecision | null;
    reason: string;
    isReasonRequired: boolean;
    showErrors: boolean;
    onDecisionChange: (d: ConfidentialityDecision) => void;
    onReasonChange: (r: string) => void;
}

const DECISION_OPTIONS = [
    { value: ConfidentialityDecision.SWITCH_OFF, labelId: 'case-closing-confidentiality.switch-off' },
    { value: ConfidentialityDecision.KEEP, labelId: 'case-closing-confidentiality.keep' },
];

function RequiredMark() {
    return <span className="text-destructive">&thinsp;*</span>;
}

export function ConfidentialitySection({
    decision,
    reason,
    isReasonRequired,
    showErrors,
    onDecisionChange,
    onReasonChange,
}: ConfidentialitySectionProps) {
    const id = useId();
    const labelId = `${id}-label`;
    const reasonId = `${id}-reason`;
    const reasonHelperId = `${id}-reason-helper`;
    const isDecisionMissing = showErrors && decision === null;
    const isReasonMissing =
        showErrors && decision === ConfidentialityDecision.KEEP && isReasonRequired && reason.trim() === '';

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
                <span id={labelId} className={cn('text-fg-secondary text-sm', isDecisionMissing && 'text-destructive')}>
                    <FormattedMessage id="case-closing-confidentiality.label" />
                    <RequiredMark />
                </span>
                <p className="text-body2">
                    <FormattedMessage id="case-closing-confidentiality.text" />
                </p>
                <RadioGroup
                    aria-labelledby={labelId}
                    className="gap-0"
                    value={decision}
                    onValueChange={(value) => onDecisionChange(value as ConfidentialityDecision)}
                >
                    {DECISION_OPTIONS.map((option) => (
                        <label
                            key={option.value}
                            className="text-fg flex h-8.5 w-fit cursor-pointer flex-row items-center gap-2 text-sm"
                        >
                            <RadioGroupItem value={option.value} aria-invalid={isDecisionMissing || undefined} />
                            {t(option.labelId)}
                        </label>
                    ))}
                </RadioGroup>
                {isDecisionMissing ? <FieldError>{t('validation.required-field')}</FieldError> : null}
            </div>
            {decision === ConfidentialityDecision.KEEP ? (
                <Field className="gap-1" data-invalid={isReasonMissing || undefined}>
                    <FieldLabel
                        htmlFor={reasonId}
                        className={cn('text-fg-secondary gap-0 font-normal', isReasonMissing && 'text-destructive')}
                    >
                        <FormattedMessage id="case-closing-confidentiality.reason-label" />
                        {isReasonRequired ? <RequiredMark /> : null}
                    </FieldLabel>
                    <Textarea
                        id={reasonId}
                        value={reason}
                        onChange={(event) => onReasonChange(event.target.value)}
                        aria-required={isReasonRequired}
                        aria-invalid={isReasonMissing || undefined}
                        aria-describedby={reasonHelperId}
                    />
                    <FieldDescription id={reasonHelperId} className="text-xs">
                        <FormattedMessage id="case-closing-confidentiality.reason-helper" />
                    </FieldDescription>
                    {isReasonMissing ? <FieldError>{t('validation.required-field')}</FieldError> : null}
                </Field>
            ) : null}
            <p className="text-fg-secondary text-xs">
                <FormattedMessage id="case-closing-confidentiality.note" />
            </p>
        </div>
    );
}
